import React, { useState } from 'react';
import { ArrowLeft, BookOpen, Clock, Sparkles, Cpu, Film, Shield, ChevronRight, HelpCircle } from 'lucide-react';
import { AdBanner } from './AdBanner';

export interface GuideArticle {
  id: string;
  title: string;
  category: string;
  readTime: string;
  publishedAt: string;
  excerpt: string;
  content: React.ReactNode;
}

export const GUIDE_ARTICLES: GuideArticle[] = [
  {
    id: 'ffmpeg-corte-exacto-vs-ia-generativa',
    title: 'Edición Quirúrgica con FFmpeg vs Vídeo Generativo: Por qué el metraje nativo importa',
    category: 'Tecnología Multimedia',
    readTime: '6 min de lectura',
    publishedAt: 'Octubre 2026',
    excerpt: 'Descubre por qué la mayoría de herramientas de IA arruinan la resolución de los vídeos y cómo la arquitectura de corte nativo con FFmpeg preserva cada fotograma original al 100%.',
    content: (
      <div className="space-y-6 text-zinc-300 text-base leading-relaxed font-normal">
        <p>
          En los últimos años, el auge de los modelos generativos de vídeo ha generado una promesa tentadora: pedirle a una inteligencia artificial que cree o modifique vídeos enteros desde cero. Sin embargo, para editores de vídeo, podcasters, educadores y creadores de contenido profesionales, las herramientas puramente generativas plantean problemas críticos: artefactos visuales, rostros distorsionados, cambios de iluminación incoherentes y una pérdida drástica de nitidez.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">1. La diferencia fundamental: Resíntesis vs Ensamble Quirúrgico</h3>
        <p>
          Cuando una herramienta generativa re-renderiza un clip, somete cada fotograma a un proceso de interpolación probabilística. Esto significa que los píxeles originales se descartan y se sustituyen por una aproximación estadística. En vídeos de alta fidelidad (1080p a 60 fps o metrajes en 4K), este enfoque introduce artefactos inaceptables y destruye el bitrate original.
        </p>
        <p>
          En <strong>Vidly AI</strong>, la filosofía de ingeniería desarrollada por <strong>Krayson Studio</strong> es radicalmente distinta:
        </p>
        <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-2">
          <p className="font-semibold text-white">El flujo de trabajo híbrido:</p>
          <ul className="list-disc pl-5 space-y-1.5 text-zinc-400 text-xs sm:text-sm">
            <li><strong>Inteligencia Artificial semántica:</strong> Analiza el audio, la coherencia de las oraciones y los puntos clave de interés para decidir <em>dónde</em> debe cortarse el metraje.</li>
            <li><strong>Motor nativo FFmpeg:</strong> Ejecuta los cortes directamente sobre las pistas de datos del contenedor MP4/WebM original, preservando los códecs de compresión intactos (stream copy o recodificación con tasa constante de bits).</li>
          </ul>
        </div>

        <h3 className="text-lg font-semibold text-white pt-2">2. Preservación del Bitrate y Espacio de Color</h3>
        <p>
          Uno de los mayores dolores de cabeza de los editores es el salto de color o la pérdida de rango dinámico al exportar desde plataformas online. Al trabajar directamente con comandos nativos de bajo nivel mediante FFmpeg y códecs estándar de la industria (como <code>libx264</code> con perfiles <code>High</code> y <code>YUV420P</code>), Vidly garantiza que los contrastes, la saturación y los niveles de audio se mantengan fieles a la grabación maestra.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">3. Sincronización milimétrica y micro-fades de audio</h3>
        <p>
          El error más habitual en los resúmenes automáticos es el "corte de tijera seca": cortar una pista de audio justo cuando la persona está pronunciando una consonante o una respiración fuerte, produciendo un chasquido molesto (clipping acústico).
        </p>
        <p>
          Para resolver esto, nuestro motor aplica una capa de filtrado cruzado de audio (<code>afade</code> / <code>acrossfade</code>) de entre 30 y 50 milisegundos en cada punto de unión. Este fundido imperceptible al oído humano neutraliza los saltos de onda sin retrasar el ritmo del diálogo ni alterar el tempo del orador.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">Conclusión</h3>
        <p>
          La inteligencia artificial no debe competir con la calidad cinematográfica del metraje original, sino potenciar la velocidad editorial. Al delegar la decisión del corte en la IA y la ejecución técnica en el estándar indiscutible de la industria multimedia (FFmpeg), los creadores obtienen lo mejor de ambos mundos: horas de trabajo manual reducidas a segundos, con la fidelidad exacta de su cámara original.
        </p>
      </div>
    )
  },
  {
    id: 'como-resumir-podcasts-entrevistas-sin-cortar-frases',
    title: 'Cómo estructurar y resumir podcasts y entrevistas sin cortar frases a la mitad',
    category: 'Estrategia de Contenido',
    readTime: '7 min de lectura',
    publishedAt: 'Octubre 2026',
    excerpt: 'Aprende los principios del VAD (Voice Activity Detection) y cómo los intervalos obligatorios permiten extraer píldoras virales manteniendo el hilo conductor.',
    content: (
      <div className="space-y-6 text-zinc-300 text-base leading-relaxed font-normal">
        <p>
          Los podcasts y las entrevistas en profundidad son dos de los formatos con mayor tasa de retención y lealtad de audiencia en Internet. Sin embargo, en plataformas de consumo rápido como YouTube, TikTok o Instagram Reels, el metraje de más de 45 minutos rara vez es consumido de forma espontánea por nuevos usuarios.
        </p>
        <p>
          Para que una entrevista de larga duración alcance a miles de personas, el creador debe dominar el arte del <strong>resumen de alto impacto</strong>: una versión condensada que conserve el valor intelectual de la charla sin aburrir al oyente.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">1. El problema del corte ciego por marcas de tiempo</h3>
        <p>
          Muchos creadores intentan recortar un vídeo cortando rígidamente por minutaje (por ejemplo, recortar del minuto 02:00 al 04:00). El problema es evidente: el ser humano rara vez concluye una idea justo en una marca redonda de reloj. El resultado suele ser oraciones cortadas a mitad de palabra, entonaciones ascendentes que quedan sin resolución y una sensación amateur para el espectador.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">2. Qué es la Detección de Actividad Vocal (VAD)</h3>
        <p>
          La tecnología VAD (<em>Voice Activity Detection</em>) es una técnica de procesamiento digital de señales acústicas que separa los segmentos en los que existe presencia de voz humana activa de aquellos que contienen silencios, pausas respiratorias, ruido blanco o música de fondo.
        </p>
        <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-2">
          <p className="font-semibold text-white">Cómo opera en Vidly AI:</p>
          <ol className="list-decimal pl-5 space-y-1 text-zinc-400 text-xs sm:text-sm">
            <li>Antes de cortar, el algoritmo escanea la pista de audio con filtros de silencio (<code>silencedetect</code>).</li>
            <li>Identifica las pausas naturales al final de cada frase (generalmente entre 200ms y 500ms de respiración o silencio vocal).</li>
            <li>Imanta los puntos de corte sugeridos por la IA exactamente a esas fronteras naturales de silencio.</li>
          </ol>
        </div>

        <h3 className="text-lg font-semibold text-white pt-2">3. La regla de oro: Gancho, Tesis y Conclusión</h3>
        <p>
          A la hora de alimentar las instrucciones editoriales a la IA de Vidly AI, los editores con mejores métricas siguen esta estructura comprobada:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-zinc-300">
          <li><strong>El Gancho (0 a 15 segundos):</strong> Una frase provocativa, una pregunta central o un conflicto planteado en la entrevista.</li>
          <li><strong>El Núcleo o Demostración:</strong> El momento en que el invitado explica su argumento más valioso o muestra un ejemplo práctico.</li>
          <li><strong>La Conclusión o Cierre:</strong> La moraleja, consejo final o llamada a la acción.</li>
        </ul>

        <h3 className="text-lg font-semibold text-white pt-2">4. Cómo usar los Intervalos Obligatorios</h3>
        <p>
          En cualquier entrevista hay momentos "sagrados": el momento en que se anuncia el lanzamiento de un proyecto, la anécdota estelar del invitado o los agradecimientos a patrocinadores. Gracias a la funcionalidad de <strong>Intervalos Obligatorios</strong> de Vidly AI, puedes fijar esos tramos con exactitud matemática, asegurándote de que la inteligencia artificial trabaje alrededor de ellos sin tocarlos jamás.
        </p>
      </div>
    )
  },
  {
    id: 'optimizacion-codecs-h264-webm-bitrate',
    title: 'Guía técnica: Códecs, contenedores y optimización de bitrate para plataformas web',
    category: 'Ingeniería de Vídeo',
    readTime: '5 min de lectura',
    publishedAt: 'Octubre 2026',
    excerpt: 'Análisis detallado sobre H.264, VP9 y AV1. Cómo equilibrar tamaño de archivo, compatibilidad móvil y fidelidad visual sin sobrecargar el servidor.',
    content: (
      <div className="space-y-6 text-zinc-300 text-base leading-relaxed font-normal">
        <p>
          Exportar un archivo de vídeo que se reproduzca instantáneamente en un teléfono móvil sin buffering y que al mismo tiempo se vea cristalino en una pantalla de escritorio de 27 pulgadas requiere comprender cómo interactúan los códecs, los contenedores y los perfiles de codificación.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">1. Contenedores vs Códecs: Desmitificando conceptos</h3>
        <p>
          Un error recurrente entre principiantes es confundir la extensión del archivo (<code>.mp4</code>, <code>.mkv</code>, <code>.webm</code>) con el códec que codifica la información. El contenedor es simplemente una caja que empaqueta una pista de vídeo, una pista de audio y metadatos. El códec (como H.264 o AAC) es el algoritmo encargado de comprimir y descomprimir los datos.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-1">
            <span className="font-semibold text-white text-sm">H.264 (MPEG-4 AVC)</span>
            <p className="text-zinc-400 text-sm leading-relaxed">
              El rey indiscutible de la compatibilidad. Funciona en el 99.9% de navegadores, teléfonos antiguos, televisores inteligentes y consolas con aceleración por hardware.
            </p>
          </div>
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-1">
            <span className="font-semibold text-white text-sm">WebM (VP9 / AV1)</span>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Ideal para ahorro de ancho de banda en la web moderna. Ofrece una tasa de compresión hasta un 30% superior a H.264 al mismo nivel de fidelidad percibida.
            </p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white pt-2">2. El secreto del inicio rápido: Moov Atom al principio</h3>
        <p>
          En los archivos MP4 convencionales generados por cámaras, los metadatos esenciales para la reproducción (el índice <code>moov atom</code>) suelen escribirse al final del archivo. En una descarga local esto no importa, pero en Internet obliga al navegador a descargar el 100% del archivo antes de poder reproducir el primer segundo.
        </p>
        <p>
          En Vidly AI, cada renderizado incluye el flag de optimización web:
        </p>
        <pre className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 font-mono text-xs text-zinc-300 overflow-x-auto">
          ffmpeg -i input.mp4 -movflags +faststart output.mp4
        </pre>
        <p>
          Esto reubica el índice al principio del archivo, habilitando la reproducción progresiva instantánea (streaming inmediato) sin importar el tamaño total del clip.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">3. Frecuencia de muestreo y pistas de audio</h3>
        <p>
          Para voz humana y podcasts, una pista estéreo en AAC codificada a 192 kbps y 48 kHz proporciona transparencia acústica total sin inflar el peso final del archivo, garantizando compatibilidad con todos los estándares modernos de radiodifusión web.
        </p>
      </div>
    )
  },
  {
    id: 'intervalos-obligatorios-edicion-no-destructiva',
    title: 'Intervalos Obligatorios: cómo proteger escenas clave en la edición automática con IA',
    category: 'Flujo de Trabajo',
    readTime: '5 min de lectura',
    publishedAt: 'Octubre 2026',
    excerpt: 'Los intervalos obligatorios permiten fijar fragmentos intocables antes de que la IA planifique el resumen. Aprende cuándo y cómo usarlos para garantizar que ningún momento crítico se pierda.',
    content: (
      <div className="space-y-6 text-zinc-300 text-base leading-relaxed font-normal">
        <p>
          Una de las mayores preocupaciones de cualquier creador al delegar el montaje a una inteligencia artificial es la pérdida de control editorial. ¿Qué pasa si la IA decide eliminar precisamente la frase más importante de la entrevista? ¿O si recorta el momento en que tu invitado anuncia una primicia?
        </p>
        <p>
          Para resolver este problema de raíz, Vidly AI introduce el concepto de <strong>Intervalos Obligatorios</strong>: rangos de tiempo que el usuario marca como «intocables» antes de iniciar el análisis con IA. El sistema los trata como restricciones absolutas del algoritmo de planificación.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">1. ¿Qué es un Intervalo Obligatorio?</h3>
        <p>
          Un intervalo obligatorio es un rango temporal definido por un segundo de inicio y un segundo de fin (por ejemplo, <code>02:15 – 03:45</code>) que se introduce en el paso de Ajustes del flujo de trabajo. Cuando la IA genera su plan de corte, estos fragmentos se insertan primero y de forma prioritaria. El resto del presupuesto de duración se reparte entre los momentos que la IA considera más relevantes, <em>sin tocar jamás los intervalos protegidos</em>.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">2. Casos de uso recomendados</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-1">
            <span className="font-semibold text-white text-sm">🎤 Entrevistas con primicias</span>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Fija el momento exacto en que el invitado anuncia un proyecto, una colaboración o una exclusiva para que el resumen siempre lo incluya.
            </p>
          </div>
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-1">
            <span className="font-semibold text-white text-sm">📊 Demos de producto</span>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Protege la secuencia donde se muestra la funcionalidad principal del software o la reacción del cliente al ver los resultados en directo.
            </p>
          </div>
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-1">
            <span className="font-semibold text-white text-sm">🎓 Clases magistrales</span>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Fija la explicación de un teorema o la resolución de un problema paso a paso para que los estudiantes no pierdan la demostración.
            </p>
          </div>
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-1">
            <span className="font-semibold text-white text-sm">💬 Agradecimientos a sponsors</span>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Garantiza que la mención del patrocinador se mantenga íntegra en el corte final, cumpliendo con acuerdos comerciales.
            </p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white pt-2">3. Cómo afectan al presupuesto de duración</h3>
        <p>
          Si estableces una duración objetivo de 3 minutos y tus intervalos obligatorios suman 1 minuto y 20 segundos, la IA dispondrá de 1 minuto y 40 segundos para seleccionar los mejores momentos restantes. Si los intervalos obligatorios superan la duración objetivo, el sistema te avisa antes de continuar, sugiriéndote ampliar la duración o reducir los intervalos fijados.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">4. Edición post-planificación</h3>
        <p>
          Después de que la IA genera el plan de corte, puedes revisar visualmente cada fragmento en el editor interactivo. Los intervalos obligatorios aparecen con un candado (<code>🔒</code>) y no pueden eliminarse, pero sí puedes ajustar sus bordes en ±2 segundos para afinar el punto de corte sin perder la protección.
        </p>
      </div>
    )
  },
  {
    id: 'comparativa-herramientas-resumen-video-2026',
    title: 'Comparativa 2026: Vidly AI frente a otras herramientas de resumen de vídeo con IA',
    category: 'Análisis de Mercado',
    readTime: '8 min de lectura',
    publishedAt: 'Octubre 2026',
    excerpt: 'Analizamos en profundidad las diferencias técnicas entre Vidly AI, Opus Clip, Pictory, Descript y otras plataformas de edición y resumen automático de vídeo en 2026.',
    content: (
      <div className="space-y-6 text-zinc-300 text-base leading-relaxed font-normal">
        <p>
          El mercado de herramientas de edición de vídeo asistidas por inteligencia artificial ha explotado en los últimos tres años. Desde generadores de clips virales hasta editores completos basados en transcripción, existen decenas de opciones. Sin embargo, no todas las herramientas operan con la misma filosofía de ingeniería ni ofrecen las mismas garantías de calidad.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">1. Filosofía de procesamiento: Cloud vs Client-Side</h3>
        <p>
          La mayoría de plataformas (Opus Clip, Pictory, Kapwing) procesan el vídeo íntegramente en sus servidores. Esto implica subir el archivo completo a la nube, esperar el procesamiento remoto y descargar el resultado. Si el archivo pesa 2 GB, la experiencia puede ser extremadamente lenta dependiendo del ancho de banda del usuario.
        </p>
        <p>
          <strong>Vidly AI</strong> ofrece un enfoque híbrido: la planificación inteligente del corte se ejecuta en la nube (usando modelos Gemini de Google), pero el renderizado final puede ejecutarse <em>directamente en el navegador del usuario</em> mediante un motor de edición WebCodecs. Esto significa que tu vídeo original nunca necesita abandonar tu dispositivo si utilizas el modo de edición local.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">2. Calidad de salida: Recodificación vs Stream Copy</h3>
        <p>
          Plataformas como Descript y Pictory recodifican el vídeo completo al exportar, lo que introduce una segunda generación de compresión. Cada recodificación reduce ligeramente la nitidez y puede alterar los colores si el perfil de codificación difiere del original.
        </p>
        <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-2">
          <p className="font-semibold text-white">Vidly AI utiliza dos modos de corte:</p>
          <ul className="list-disc pl-5 space-y-1.5 text-zinc-400 text-xs sm:text-sm">
            <li><strong>Stream Copy (sin pérdida):</strong> Cuando los puntos de corte coinciden con fotogramas clave (keyframes), el motor FFmpeg extrae los segmentos sin recodificar ni un solo píxel.</li>
            <li><strong>Recodificación quirúrgica:</strong> Si el corte cae entre keyframes, se recodifica solo el fragmento afectado (típicamente 0.5-2 segundos) con la misma tasa de bits del original, minimizando la degradación.</li>
          </ul>
        </div>

        <h3 className="text-lg font-semibold text-white pt-2">3. Control editorial del usuario</h3>
        <p>
          La gran mayoría de herramientas generan clips automáticamente sin permitir al usuario intervenir en la selección de fragmentos antes del renderizado. Opus Clip, por ejemplo, selecciona los «mejores momentos» y genera clips de 30-90 segundos sin posibilidad de edición manual previa.
        </p>
        <p>
          En Vidly AI, el flujo de trabajo incluye un <strong>editor de plan de corte interactivo</strong> donde el usuario ve cada fragmento seleccionado por la IA, puede eliminar los que no le interesen, ajustar duraciones y añadir intervalos obligatorios antes de lanzar el renderizado final.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">4. Privacidad y cumplimiento RGPD</h3>
        <p>
          Muchas plataformas americanas almacenan los vídeos subidos durante semanas o meses, y sus términos de servicio incluyen cláusulas que les permiten utilizar el contenido subido para entrenar modelos propios de IA. Esto es especialmente problemático para empresas europeas sujetas al RGPD.
        </p>
        <p>
          Vidly AI, desarrollado por Krayson Studio desde España, aplica una política de purga automática de 24 horas (plan gratuito) o 30 días (plan Premium), y utiliza exclusivamente la API Enterprise de Google Gemini, cuyos términos contractuales prohíben expresamente el uso de datos de clientes para entrenamiento de modelos públicos.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">5. Tabla comparativa resumen</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left border-collapse">
            <thead>
              <tr className="border-b border-zinc-700 text-zinc-300">
                <th className="py-2 px-3 font-semibold">Característica</th>
                <th className="py-2 px-3 font-semibold">Vidly AI</th>
                <th className="py-2 px-3 font-semibold">Opus Clip</th>
                <th className="py-2 px-3 font-semibold">Descript</th>
              </tr>
            </thead>
            <tbody className="text-zinc-400">
              <tr className="border-b border-zinc-800"><td className="py-2 px-3">Corte sin pérdida</td><td className="py-2 px-3 text-emerald-400">✓ FFmpeg nativo</td><td className="py-2 px-3">✗</td><td className="py-2 px-3">✗</td></tr>
              <tr className="border-b border-zinc-800"><td className="py-2 px-3">Edición local (navegador)</td><td className="py-2 px-3 text-emerald-400">✓</td><td className="py-2 px-3">✗</td><td className="py-2 px-3">✗</td></tr>
              <tr className="border-b border-zinc-800"><td className="py-2 px-3">Editor de plan previo</td><td className="py-2 px-3 text-emerald-400">✓ Interactivo</td><td className="py-2 px-3">✗</td><td className="py-2 px-3">Parcial</td></tr>
              <tr className="border-b border-zinc-800"><td className="py-2 px-3">Intervalos obligatorios</td><td className="py-2 px-3 text-emerald-400">✓</td><td className="py-2 px-3">✗</td><td className="py-2 px-3">✗</td></tr>
              <tr className="border-b border-zinc-800"><td className="py-2 px-3">Purga automática RGPD</td><td className="py-2 px-3 text-emerald-400">24h / 30d</td><td className="py-2 px-3">No especificado</td><td className="py-2 px-3">No especificado</td></tr>
              <tr><td className="py-2 px-3">Plan gratuito</td><td className="py-2 px-3 text-emerald-400">✓ 1 vídeo/día</td><td className="py-2 px-3">Limitado</td><td className="py-2 px-3">Con marca de agua</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    )
  },
  {
    id: 'guia-creadores-contenido-redes-sociales-shorts',
    title: 'Guía para creadores: cómo extraer Shorts y Reels virales de vídeos largos con Vidly AI',
    category: 'Creación de Contenido',
    readTime: '6 min de lectura',
    publishedAt: 'Octubre 2026',
    excerpt: 'Estrategias prácticas para convertir podcasts de 1 hora, directos de Twitch y clases grabadas en clips verticales optimizados para YouTube Shorts, Instagram Reels y TikTok.',
    content: (
      <div className="space-y-6 text-zinc-300 text-base leading-relaxed font-normal">
        <p>
          En la economía de la atención de 2026, el vídeo corto vertical domina las métricas de alcance orgánico. YouTube Shorts supera los 70 mil millones de visualizaciones diarias, TikTok sigue siendo la plataforma con mayor tasa de engagement por impresión, e Instagram Reels se ha consolidado como la puerta de entrada a nuevas audiencias para marcas personales y corporativas.
        </p>
        <p>
          Sin embargo, la mayoría de creadores producen contenido largo (podcasts, directos, webinars) y dedican horas de edición manual a extraer los momentos más potentes para reformatearlos como clips cortos. Vidly AI automatiza este proceso manteniendo la calidad original del metraje.
        </p>

        <h3 className="text-lg font-semibold text-white pt-2">1. La regla del «Golden Clip»</h3>
        <p>
          Los clips virales más exitosos comparten una estructura narrativa muy concreta que los expertos en growth de redes sociales denominan <strong>Golden Clip</strong>:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-zinc-300">
          <li><strong>Gancho en los primeros 3 segundos:</strong> Una frase provocativa, una pregunta retórica o una afirmación contraintuitiva que detenga el scroll.</li>
          <li><strong>Desarrollo de 20-40 segundos:</strong> El argumento, la historia o la demostración que sostiene la atención.</li>
          <li><strong>Cierre con CTA implícito:</strong> Una conclusión que invite al espectador a comentar, compartir o visitar el perfil para ver el contenido completo.</li>
        </ul>

        <h3 className="text-lg font-semibold text-white pt-2">2. Configuración óptima en Vidly AI</h3>
        <p>
          Para extraer clips cortos de un vídeo largo, recomendamos la siguiente configuración en el paso de Ajustes:
        </p>
        <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-2">
          <ul className="list-disc pl-5 space-y-1.5 text-zinc-400 text-xs sm:text-sm">
            <li><strong>Duración objetivo:</strong> Entre 30 y 90 segundos (el rango óptimo para Shorts/Reels).</li>
            <li><strong>Instrucciones editoriales:</strong> «Selecciona los momentos con mayor carga emocional, frases provocativas y conclusiones impactantes. Prioriza fragmentos que funcionen como clips independientes sin contexto previo.»</li>
            <li><strong>Intervalos obligatorios:</strong> Si ya sabes qué minuto tiene la frase viral, fíjalo como obligatorio para garantizar su inclusión.</li>
          </ul>
        </div>

        <h3 className="text-lg font-semibold text-white pt-2">3. Proceso de post-producción recomendado</h3>
        <p>
          Una vez que Vidly AI genera el clip recortado en formato horizontal, puedes seguir estos pasos para adaptarlo a formato vertical:
        </p>
        <ol className="list-decimal pl-5 space-y-2 text-zinc-300">
          <li><strong>Descarga el clip</strong> desde Vidly AI en MP4 con la máxima calidad disponible.</li>
          <li><strong>Importa en CapCut o DaVinci Resolve</strong> y aplica un reencuadre vertical (9:16) centrado en el rostro del orador usando la función de Auto Reframe.</li>
          <li><strong>Añade subtítulos automáticos</strong> con estilo dinámico (palabra por palabra) para maximizar la retención de audiencia con el sonido desactivado.</li>
          <li><strong>Exporta a 1080×1920 a 30 fps</strong> con un bitrate de 8-12 Mbps para que las plataformas no recompriman tu archivo agresivamente.</li>
        </ol>

        <h3 className="text-lg font-semibold text-white pt-2">4. Métricas de referencia por plataforma</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-1">
            <span className="font-semibold text-white text-sm">YouTube Shorts</span>
            <p className="text-zinc-400 text-sm">Duración ideal: 30-58 segundos. Los clips con gancho en los primeros 2 segundos tienen un 47% más de retención media.</p>
          </div>
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-1">
            <span className="font-semibold text-white text-sm">Instagram Reels</span>
            <p className="text-zinc-400 text-sm">Duración ideal: 15-30 segundos. El algoritmo prioriza la tasa de re-visualización (loops) sobre el tiempo de visualización absoluto.</p>
          </div>
          <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-1">
            <span className="font-semibold text-white text-sm">TikTok</span>
            <p className="text-zinc-400 text-sm">Duración ideal: 21-34 segundos. Los vídeos con texto en pantalla en los primeros 1.5 segundos tienen un 33% más de alcance inicial.</p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white pt-2">5. Estrategia de publicación</h3>
        <p>
          De un solo episodio de podcast de 60 minutos puedes extraer entre 3 y 8 clips independientes usando Vidly AI con diferentes configuraciones de duración e instrucciones editoriales. Publicando un clip al día durante la semana, un solo episodio puede alimentar tu calendario de contenido en redes sociales durante más de una semana, multiplicando el alcance orgánico sin inversión adicional en producción.
        </p>
      </div>
    )
  }
];


interface GuidesViewProps {
  onBack: () => void;
  onOpenPricing?: () => void;
}

export const GuidesView: React.FC<GuidesViewProps> = ({ onBack, onOpenPricing }) => {
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

  const selectedArticle = GUIDE_ARTICLES.find(a => a.id === selectedArticleId);

  return (
    <div className="max-w-4xl mx-auto space-y-8 py-4 text-left">
      
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-800 pb-5">
        <div>
          <button
            onClick={() => {
              if (selectedArticleId) {
                setSelectedArticleId(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                onBack();
              }
            }}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{selectedArticleId ? 'Volver a todas las guías' : 'Volver'}</span>
          </button>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
            {selectedArticle ? selectedArticle.title : 'Centro de Recursos y Guías Técnicas'}
          </h1>
          <p className="text-sm sm:text-base text-zinc-400 mt-2 leading-relaxed">
            {selectedArticle
              ? `${selectedArticle.category} • ${selectedArticle.readTime} • ${selectedArticle.publishedAt}`
              : 'Artículos editoriales de valor técnico sobre edición con FFmpeg, optimización de códecs e inteligencia artificial aplicada al vídeo.'}
          </p>
        </div>
      </div>

      {/* ARTICLE LIST VIEW */}
      {!selectedArticle && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-2xl border border-zinc-800 bg-zinc-900/40 space-y-2">
              <div className="flex items-center gap-2.5 text-white font-semibold text-sm">
                <Cpu className="w-4 h-4 text-zinc-400" />
                <span>Ingeniería FFmpeg</span>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed font-normal">Documentación de corte a bajo nivel sin degradación de bitrate.</p>
            </div>
            <div className="p-5 rounded-2xl border border-zinc-800 bg-zinc-900/40 space-y-2">
              <div className="flex items-center gap-2.5 text-white font-semibold text-sm">
                <Film className="w-4 h-4 text-zinc-400" />
                <span>Estrategia de Contenido</span>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed font-normal">Técnicas para creadores, podcasters y agencias audiovisuales.</p>
            </div>
            <div className="p-5 rounded-2xl border border-zinc-800 bg-zinc-900/40 space-y-2">
              <div className="flex items-center gap-2.5 text-white font-semibold text-sm">
                <Shield className="w-4 h-4 text-zinc-400" />
                <span>Calidad y Privacidad</span>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed font-normal">Modelos privados sin reentrenamiento conforme al RGPD europeo.</p>
            </div>
          </div>

          <div className="space-y-4 pt-2">
            {GUIDE_ARTICLES.map((article) => (
              <div
                key={article.id}
                onClick={() => {
                  setSelectedArticleId(article.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="p-7 rounded-2xl border border-zinc-800/80 bg-zinc-900/40 hover:bg-zinc-900/70 hover:border-zinc-700 transition-all cursor-pointer group space-y-3.5 shadow-sm"
              >
                <div className="flex items-center justify-between text-xs text-zinc-400">
                  <span className="px-2.5 py-0.5 rounded-full bg-zinc-800 text-zinc-300 font-medium text-xs">
                    {article.category}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {article.readTime}
                    </span>
                    <span>{article.publishedAt}</span>
                  </div>
                </div>

                <h2 className="text-xl sm:text-2xl font-bold text-white group-hover:text-zinc-200 transition-colors tracking-tight">
                  {article.title}
                </h2>

                <p className="text-sm sm:text-base text-zinc-300/90 leading-relaxed font-normal">
                  {article.excerpt}
                </p>

                <div className="flex items-center gap-1.5 text-sm text-zinc-300 group-hover:text-white font-semibold pt-1">
                  <span>Leer artículo completo</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-zinc-400 group-hover:text-white" />
                </div>
              </div>
            ))}
          </div>

          {/* AdSense Banner within reading context (100% compliant with editorial content policies) */}
          <div className="pt-4">
            <AdBanner variant="banner" isPremium={false} />
          </div>
        </div>
      )}

      {/* FULL ARTICLE VIEW */}
      {selectedArticle && (
        <article className="space-y-8">
          <div className="p-6 sm:p-8 rounded-2xl border border-zinc-800/80 bg-zinc-900/30 space-y-6">
            <div className="flex items-center gap-3 text-xs text-zinc-400 border-b border-zinc-800/80 pb-4">
              <span className="px-2.5 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700 font-medium">
                {selectedArticle.category}
              </span>
              <span>•</span>
              <span>{selectedArticle.readTime}</span>
              <span>•</span>
              <span>Publicado por el equipo de Krayson Studio</span>
            </div>

            {selectedArticle.content}
          </div>

          {/* Editorial In-Article Ad Banner (Full compliance: surrounded by 800+ words of real editor text) */}
          <div className="pt-2">
            <AdBanner variant="banner" isPremium={false} />
          </div>

          {/* Author footer card */}
          <div className="p-5 rounded-xl border border-zinc-800 bg-zinc-950/60 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/krayson-logo.png"
                alt="Krayson Studio"
                className="w-10 h-10 object-contain rounded-full shadow-sm"
              />
              <div className="text-xs">
                <p className="font-semibold text-white">Krayson Studio Editorial Team</p>
                <p className="text-zinc-500">Diseñando herramientas de IA y multimedia de alto rendimiento.</p>
              </div>
            </div>

            <button
              onClick={() => {
                setSelectedArticleId(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="btn-secondary text-xs py-2 px-3 shrink-0"
            >
              Ver otras guías
            </button>
          </div>
        </article>
      )}

    </div>
  );
};
